import logError from 'App/babel/helpers/logError';

/**
 * What a viewer had picked in a Page's `useFilter` controls, kept per history entry so Back
 * from a record brings the Page back as they left it (WEB-13939). Opening the Page anew is a
 * new entry, so it starts from the Page's defaults.
 *
 * Kept by the host rather than the shell: the shell is meant to move to an opaque origin
 * (WEB-6726), where it has no storage of its own.
 */

const STORAGE_PREFIX = 'upsales.page.filters.';
const ENTRY_KEY = 'upsalesPageEntry';
// One Page's filters are a few ids and strings. Anything near this is a Page storing data in
// a filter, and sessionStorage is shared with the whole app.
const MAX_CHARS = 2_000;

/**
 * This history entry's own id. Hash navigation leaves `history.state` empty, so the first
 * Page mounted on an entry stamps one; Back restores the entry and its state with it.
 */
export function historyEntryKey(): string {
	const state = window.history.state;
	const existing = state && typeof state === 'object' ? (state as Record<string, unknown>)[ENTRY_KEY] : undefined;
	if (typeof existing === 'string') return existing;
	const key = Math.random().toString(36).slice(2);
	const base = state && typeof state === 'object' ? state : {};
	window.history.replaceState({ ...base, [ENTRY_KEY]: key }, '', window.location.href);
	return key;
}

export function readPageFilters(memoryKey: string): Record<string, unknown> | null {
	try {
		const raw = window.sessionStorage.getItem(STORAGE_PREFIX + memoryKey);
		return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
	} catch (err) {
		logError(err, '[pageFilterMemory] could not read remembered filters');
		return null;
	}
}

export function rememberPageFilter(memoryKey: string, name: string, value: unknown): void {
	try {
		const next = JSON.stringify({ ...readPageFilters(memoryKey), [name]: value });
		if (next.length > MAX_CHARS) {
			logError(new Error(`filters over ${MAX_CHARS} chars`), '[pageFilterMemory] not remembering filters');
			return;
		}
		window.sessionStorage.setItem(STORAGE_PREFIX + memoryKey, next);
	} catch (err) {
		logError(err, '[pageFilterMemory] could not remember filters');
	}
}
